<script>
  import Section from '../components/Section.svelte';
  import Panel from '../components/Panel.svelte';
  import Alert from '../components/Alert.svelte';
  import Button from '../components/Button.svelte';
  import Stepper from '../components/Stepper.svelte';
  import DataTable from '../components/DataTable.svelte';
  import Code from '../components/Code.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import Async from '../components/Async.svelte';
  import { draft } from '../lib/partnerDraft.svelte.js';
  import { selectedSlices, datasetLabel } from '../lib/slices.js';
  import { publish, getConfig, getDatasets } from '../lib/api.js';
  import { SELF } from '../../shared/config.js';
  import { HELP } from '../lib/help.js';

  const steps = ['Partner details', 'What you share', 'Review'];
  // aggregations.json read live from the Pod, to preview how many cubes the
  // selected datasets expand to (the backend recomputes this from the same config).
  const configPromise = Promise.all([getConfig('aggregations'), getDatasets()])
    .then(([aggregations, { datasets }]) => ({ aggregations, datasets }));

  let publishing = $state(false);
  let error = $state(null);
  const submittedInbound = () => draft.inboundContainers
    .filter((item) => item.url.trim())
    .map((item) => ({ url: item.url.trim(), ...(item.label.trim() ? { label: item.label.trim() } : {}) }));

  async function confirm() {
    publishing = true; error = null; draft.result = null;
    try {
      // The backend writes the slices, the manifest and the .acl as the
      // institutional WebID — and rejects this call outright unless the login
      // holds the manager role.
      draft.result = await publish({
        label: draft.name,
        contact: draft.email,
        webId: draft.webId,
        datasets: draft.datasets,
        inboundContainers: submittedInbound(),
      });
    } catch (e) {
      error = e.message;
    } finally {
      publishing = false;
    }
  }

  const aclPreview = `# Written to <container>/.acl
<#partner-read>
  a acl:Authorization ;
  acl:agent <${draft.webId}> ;
  acl:accessTo <…/shared/{partner}/> ;
  acl:default <…/shared/{partner}/> ;
  acl:mode acl:Read .`;
</script>

<HelpDrawer {...HELP['partner/confirm']}>
  {#snippet children()}
    <p class="help-prose">The WAC rule written to the partner container:</p>
    <Code lang="turtle" text={aclPreview} />
  {/snippet}
</HelpDrawer>

<h1 class="wf2-title wf2-title-level-1">Add sharing partner</h1>
<Stepper {steps} active={2} />

<Section title="Summary">
  <Panel>
    <Async promise={configPromise} loading="Reading datasets from your Pod…">
      {#snippet children({ aggregations, datasets })}
      {@const slices = selectedSlices(aggregations, draft.datasets)}
      {@const inbound = submittedInbound()}
      <DataTable
        columns={['Field', 'Value']}
        rows={[
          ['Organisation', draft.name],
          ['Contact', draft.email],
          ['Partner WebID', draft.webId],
          ['Datasets', Object.entries(draft.datasets).filter(([, v]) => v).map(([k]) => datasetLabel(datasets, k)).join(', ') || '(none)'],
          ['Cubes to write', `${slices.length}`],
          ['What they share with you', inbound.length ? `${inbound.length} place(s) they pointed you at` : '(none)'],
          ['Owner (you)', SELF.webId],
        ]}
      />
      {#if slices.length === 0 && inbound.length === 0}
        <Alert title="Nothing is being shared either way" variant="warning">
          <p class="wf2-text">Pick a dataset to share with them, or add somewhere they share with you.</p>
        </Alert>
      {/if}
      {#if inbound.length}
        <DataTable
          columns={['What they share with you', 'Label']}
          rows={inbound.map((item) => [item.url, item.label || '—'])}
        />
      {/if}
      <Alert title="Confirming updates your partner list" variant="info">
        <p class="wf2-text">
          What they share with you is recorded and checked as your institution —
          the app only ever reads it, never writes. {#if slices.length}It also
          puts a copy of the cubes you chose where they can reach it, and gives
          them permission to read it.{/if}
        </p>
      </Alert>
      <div class="wf2-row" style="gap:12px">
        <Button variant="primary" onclick={confirm} disabled={publishing || (slices.length === 0 && inbound.length === 0)}>
          {publishing ? 'Saving…' : 'Confirm partnership'}
        </Button>
        <Button variant="secondary" to="partner/access">Back</Button>
      </div>
      {/snippet}
    </Async>
  </Panel>
</Section>

{#if error}
  <Alert title="Publish failed" variant="danger"><p class="mono">{error}</p></Alert>
{/if}

{#if draft.result}
  <Section title={draft.result.ok ? 'Partnership saved' : 'Completed with errors'}>
    <Panel>
      <Alert title={draft.result.ok ? 'Directional sharing recorded' : 'Some outbound steps failed'} variant={draft.result.ok ? 'success' : 'warning'}>
        <p class="wf2-text">
          {#if draft.result.outbound}
            Written to <code class="mono">{draft.result.outbound.container}</code>.
            {draft.result.outbound.slices.length} cube(s) copied for the partner.
          {:else}
            Nothing was sent to them, and nobody was given access.
          {/if}
          {draft.result.inbound.containers.length} place(s) they share with you
          recorded and checked.
        </p>
      </Alert>
      {#if draft.result.inbound.containers.length}
        <DataTable
          columns={['What they share with you', 'Status']}
          rows={draft.result.inbound.containers.map((item) => [
            item.url,
            item.status === 200 ? 'you can read it' : [401, 403].includes(item.status) ? `they haven't shared it yet (${item.status})` : `can't reach it (${item.status || 'no response'})`,
          ])}
        />
      {/if}
      {#if draft.result.outbound}
        <div class="wf2-tablewrap" style="overflow-x:auto">
          <table class="wf2-table">
            <thead><tr><th>Outbound step</th><th>HTTP</th><th>Result</th></tr></thead>
            <tbody>
              {#each draft.result.outbound.steps as s}
                <tr><td class="mono">{s.label}</td><td>{s.status}</td><td>{s.ok ? 'ok' : 'failed'}</td></tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
      <div class="wf2-row" style="gap:12px">
        <Button variant="primary" to="partners">See in partner list</Button>
        <Button variant="secondary" to="catalog">See it in the catalog</Button>
        <Button variant="secondary" to="overview">Back to overview</Button>
      </div>
    </Panel>
  </Section>
{/if}
